import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { isNativeEnvironment } from '@/hooks/useNativeApp';

interface SubscriptionContextType {
  subscribed: boolean;
  subscriptionTier: string;
  subscriptionEnd: string | null;
  loading: boolean;
  checkSubscription: () => Promise<void>;
  createCheckout: (plan?: string) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  dailyUsage: {
    ai_prompts_used: number;
    remaining_prompts: number;
  };
  canUseFeature: (feature: string) => boolean;
  incrementUsage: (feature: string) => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { user, session } = useAuth();
  const { toast } = useToast();
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [dailyUsage, setDailyUsage] = useState({
    ai_prompts_used: 0,
    remaining_prompts: 2,
  });

  // Check subscription status
  const checkSubscription = async () => {
    if (!user || !session) return;

    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      setSubscribed(data.subscribed || false);
      setSubscriptionTier(data.subscription_tier || 'free');
      setSubscriptionEnd(data.subscription_end);

      // Load daily usage if on free tier
      if (data.subscription_tier === 'free') {
        await loadDailyUsage();
      }
    } catch (error: any) {
      console.error('Error checking subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load daily usage for free tier users
  const loadDailyUsage = async () => {
    if (!user) return;

    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('daily_usage')
        .select('ai_prompts_used')
        .eq('user_id', user.id)
        .eq('usage_date', today)
        .eq('feature_type', 'ai_prompts')
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        console.error('Error loading daily usage:', error);
        return;
      }

      const used = data?.ai_prompts_used || 0;
      setDailyUsage({
        ai_prompts_used: used,
        remaining_prompts: Math.max(0, 2 - used),
      });
    } catch (error) {
      console.error('Error loading daily usage:', error);
    }
  };

  // Create checkout session
  const createCheckout = async (plan = 'monthly') => {
    if (!session) return;

    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        // Check if this is a native app blocking error
        if (error.message?.includes('native app') || error.message?.includes('App Store')) {
          // Redirect to the native purchase restricted page
          window.location.href = '/native-purchase-restricted';
          return;
        }
        throw error;
      }

      // Redirect to Stripe checkout (iPad/Safari compatible)
      window.location.href = data.url;
    } catch (error: any) {
      console.error('Error creating checkout:', error);
      toast({
        title: "Checkout Error",
        description: error.message || "Failed to create checkout session",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Open customer portal
  const openCustomerPortal = async () => {
    if (!session) return;

    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        // Check if this is a native app blocking error
        if (error.message?.includes('native app') || error.message?.includes('App Store')) {
          // Redirect to the native purchase restricted page
          window.location.href = '/native-purchase-restricted';
          return;
        }
        throw error;
      }

      // Redirect to customer portal (iPad/Safari compatible)
      window.location.href = data.url;
    } catch (error: any) {
      console.error('Error opening customer portal:', error);
      toast({
        title: "Portal Error",
        description: error.message || "Failed to open customer portal",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if user can use a feature
  const canUseFeature = (feature: string) => {
    if (subscriptionTier === 'pro') return true;

    switch (feature) {
      case 'ai_prompts':
        return dailyUsage.remaining_prompts > 0;
      case 'advanced_meal_planning':
        return false; // Pro only
      case 'family_sharing':
        return false; // Pro only
      default:
        return true; // Core features are free
    }
  };

  // Increment usage counter
  const incrementUsage = async (feature: string) => {
    if (!user || subscriptionTier === 'pro') return;

    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('daily_usage')
        .upsert({
          user_id: user.id,
          usage_date: today,
          feature_type: feature,
          ai_prompts_used: feature === 'ai_prompts' ? dailyUsage.ai_prompts_used + 1 : 0,
        }, {
          onConflict: 'user_id,usage_date,feature_type',
        });

      if (error) throw error;

      // Update local state
      if (feature === 'ai_prompts') {
        setDailyUsage(prev => ({
          ai_prompts_used: prev.ai_prompts_used + 1,
          remaining_prompts: Math.max(0, prev.remaining_prompts - 1),
        }));
      }
    } catch (error) {
      console.error('Error incrementing usage:', error);
    }
  };

  // Check subscription on auth changes
  useEffect(() => {
    if (user && session) {
      checkSubscription();
    } else {
      // Reset state when user logs out
      setSubscribed(false);
      setSubscriptionTier('free');
      setSubscriptionEnd(null);
      setDailyUsage({ ai_prompts_used: 0, remaining_prompts: 2 });
    }
  }, [user, session]);

  // Refresh subscription status every 10 seconds when user is on success page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true' && user && session) {
      const interval = setInterval(checkSubscription, 10000);
      return () => clearInterval(interval);
    }
  }, [user, session]);

  const value = {
    subscribed,
    subscriptionTier,
    subscriptionEnd,
    loading,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    dailyUsage,
    canUseFeature,
    incrementUsage,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};